/**
 * dedupeAndSort - Deduplicates and alphabetically sorts an array of
 * { value, label } option objects for use in dropdowns/selects.
 *
 * Removes duplicate entries by both `value` AND `label` (keeps the first
 * occurrence of each), then sorts alphabetically by `label` (A-Z, case-insensitive).
 * This prevents two records with different IDs but identical display names
 * from appearing as duplicates in the dropdown.
 *
 * Use this for ALL dynamic dropdowns: users, contacts, companies,
 * locations, cities, states, etc.
 *
 * @param items - Array of objects with at least `value` and `label` keys
 * @returns Deduped and sorted array
 *
 * @example
 * const options = dedupeAndSort([
 *	   { value: '2', label: 'Zebra Corp' },
 *	   { value: '1', label: 'Alpha Inc' },
 *	   { value: '2', label: 'Zebra Corp' },	 // duplicate
 * ]);
 * // Result: [{ value: '1', label: 'Alpha Inc' }, { value: '2', label: 'Zebra Corp' }]
 */
export const dedupeAndSort = <T extends { value: string; label: string }>(items: T[]): T[] => {
	/* Track both value (ID) and label (display text) to prevent duplicates.
	 * Two records with different IDs but the same label appear as dupes to users. */
	const seenValues = new Set<string>();
	const seenLabels = new Set<string>();
	return items
		.filter((item) => {
			if (seenValues.has(item.value)) return false;
			/* Case-insensitive label check so "Acme Corp" and "acme corp" are treated as dupes */
			const normalizedLabel = item.label.toLowerCase().trim();
			if (seenLabels.has(normalizedLabel)) return false;
			seenValues.add(item.value);
			seenLabels.add(normalizedLabel);
			return true;
		})
		.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

/**
 * dedupeAndSortStrings - Deduplicates and alphabetically sorts a plain
 * string array. Useful for city/state filter dropdowns.
 *
 * Null/undefined/empty entries are dropped, so callers can pass optional
 * fields (e.g. location city/state, which are nullable) straight through.
 *
 * @param items - Array of strings, possibly with null/undefined holes
 * @returns Deduped and sorted array
 *
 * @example
 * dedupeAndSortStrings(['Indianapolis', 'Carmel', 'Indianapolis'])
 * // Result: ['Carmel', 'Indianapolis']
 */
export const dedupeAndSortStrings = (items: (string | null | undefined)[]): string[] => {
	/* Case-insensitive dedup so "Indianapolis" and "indianapolis" aren't both shown */
	const seen = new Set<string>();
	return items
		.filter((item): item is string => Boolean(item))
		.filter((item) => {
			const normalized = item.toLowerCase().trim();
			if (seen.has(normalized)) return false;
			seen.add(normalized);
			return true;
		})
		.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};
